import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [cheerCount, setCheerCount] = useState(() => {
    return parseInt(localStorage.getItem('broweekend_cheer_count') || '42', 10);
  });
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationIdRef = useRef(null);

  // Confetti particles animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Initial festive burst
    for (let i = 0; i < 70; i++) {
      particlesRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 3,
        vy: Math.random() * 3 + 1,
        size: Math.random() * 6 + 2,
        color: `hsl(${Math.random() * 360}, 100%, 65%)`,
        alpha: Math.random() * 0.8 + 0.2,
        rotation: Math.random() * Math.PI * 2,
        vRot: (Math.random() - 0.5) * 0.1
      });
    }

    const addGlitter = () => {
      if (particlesRef.current.length < 120) {
        for (let i = 0; i < 2; i++) {
          particlesRef.current.push({
            x: Math.random() * canvas.width,
            y: -10,
            vx: (Math.random() - 0.5) * 2,
            vy: Math.random() * 2 + 1,
            size: Math.random() * 5 + 2,
            color: `hsl(${Math.random() * 360}, 100%, 60%)`,
            alpha: 1,
            rotation: Math.random() * Math.PI * 2,
            vRot: (Math.random() - 0.5) * 0.1
          });
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      addGlitter();

      particlesRef.current = particlesRef.current.filter(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.vRot;
        p.alpha -= 0.005;
        p.vy += 0.05;

        if (p.alpha > 0 && p.y < canvas.height + 20) {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.alpha;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
          ctx.restore();
          return true;
        }
        return false;
      });

      animationIdRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationIdRef.current);
    };
  }, []);

  // Trigger confetti burst
  const triggerConfetti = (originX, originY) => {
    const x = originX || window.innerWidth / 2;
    const y = originY || window.innerHeight / 2;

    for (let i = 0; i < 80; i++) {
      const angle = (Math.PI * 2 * i) / 80;
      const speed = 3 + Math.random() * 7;
      particlesRef.current.push({
        x: x,
        y: y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.random() * 7 + 3,
        color: `hsl(${Math.random() * 360}, 100%, 65%)`,
        alpha: 1,
        rotation: Math.random() * Math.PI * 2,
        vRot: (Math.random() - 0.5) * 0.2
      });
    }
  };

  const handlePageClick = (e) => {
    if (e.target.closest('.interactive-btn') || e.target.closest('.easter-egg-btn')) return;
    triggerConfetti(e.clientX, e.clientY);
  };

  const handleCheer = (e) => {
    e.stopPropagation();
    const newCount = cheerCount + 1;
    setCheerCount(newCount);
    localStorage.setItem('broweekend_cheer_count', newCount.toString());
    triggerConfetti(e.clientX, e.clientY);
  };

  return (
    <div className="App" onClick={handlePageClick}>
      <canvas ref={canvasRef} className="particle-canvas" />

      {/* Floating Sparkles & Stars */}
      <div className="emoji-scatter emoji-top-left">✨</div>
      <div className="emoji-scatter emoji-top-right">🥂</div>
      <div className="emoji-scatter emoji-bottom-left">🥳</div>
      <div className="emoji-scatter emoji-bottom-right">🚀</div>

      <div className="festive-container">
        <div 
          className="crown-badge easter-egg-btn"
          onClick={(e) => {
            e.stopPropagation();
            setShowEasterEgg(!showEasterEgg);
            triggerConfetti(e.clientX, e.clientY);
          }}
          title="Klik voor een geheim Bro-bericht!"
        >
          👑
        </div>

        <h1 className="title">Broweekend is Voorbij! 🎉</h1>
        
        <div className="message-card">
          <p className="highlight-text">
            Het is een fantastische editie geweest! 💛✨
          </p>
          <p className="body-text">
            Bedankt aan iedereen voor alle geweldige herinneringen, de slappe lach, de legendarische momenten en de absolute Bro-liefde. 
          </p>
          <p className="future-text">
            We kunnen nu al niet wachten tot de volgende editie! 🚀🥂✨
          </p>
        </div>

        {showEasterEgg && (
          <div className="easter-egg-toast">
            🏆 <strong>Bro Code #1:</strong> Wat op het Broweekend gebeurt... is nu al legendarisch! Zie je op het volgende feestje! 🔥
          </div>
        )}

        <div className="action-buttons">
          <button className="interactive-btn cheer-btn" onClick={handleCheer}>
            🥂 Proost op de Volgende Editie! <span className="cheer-badge">{cheerCount}</span>
          </button>
          
          <button className="interactive-btn confetti-btn" onClick={(e) => { e.stopPropagation(); triggerConfetti(); }}>
            🎉 Gooi Feest Confetti!
          </button>
        </div>

        <div className="click-hint">
          <span className="hint-text">✨ Klik overal op het scherm voor extra confetti! ✨</span>
        </div>
      </div>
    </div>
  );
}

export default App;

